


























































































































import { Vue, Component, Prop } from "vue-property-decorator";
import { audio } from "@/config/host.config";
import Progress from "@/components/app/images/progress.vue";
import AudioPlayerBar from "@/components/app/audio-player-bar.vue";
import TrackEditModal from "./track-edit-modal.vue";
import StandartTable from "@/components/tables/standart-table.vue";

const playlistUrl = audio.id3Playlists;
const tracksUrl = audio.id3Tracks;
const streamTemplate = `${audio.id3Stream}?id={id}`;

interface ITrackMetadata {
  title?: string;
  artist?: string;
  album?: string;
  duration?: number;
}

interface ITrack {
  _id: string;
  name: string;
  generatedName?: string;
  path?: string;
  metadata?: ITrackMetadata;
  src?: { cover?: string };
}

@Component({
  components: {
    ProgressComponent: Progress,
    AudioPlayerBar,
    TrackEditModal,
    StandartTable,
  },
})
class PlaylistTracksEditor extends Vue {
  @Prop({ type: String, required: true }) playlistId!: string;
  @Prop({ type: Array, default: () => [] }) initialTracks!: ITrack[];

  tracks: ITrack[] = [];
  loading = false;
  uploading = false;
  uploadProgress = 0;
  totalBytes = 0;
  uploadedBytes = 0;
  streamTemplate = streamTemplate;
  currentParams: Record<string, string | number> = {};
  currentTitle = "";
  currentSubtitle = "";
  editingTrack: ITrack | null = null;

  $refs!: {
    fileInput: HTMLInputElement;
  };

  trackTitle(track: ITrack): string {
    return (track.metadata && track.metadata.title) || track.name || "—";
  }

  trackArtist(track: ITrack): string {
    return (track.metadata && track.metadata.artist) || "—";
  }

  trackAlbum(track: ITrack): string {
    return (track.metadata && track.metadata.album) || "—";
  }

  trackDuration(track: ITrack): number {
    return track.metadata ? track.metadata.duration || 0 : 0;
  }

  getCover(track: ITrack): string {
    return (track.src && track.src.cover) || "";
  }

  formatDuration(seconds: number): string {
    if (!seconds) return "—";
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, "0")}`;
  }

  playTrack(track: ITrack): void {
    if (!track._id) {
      this.$notify({
        group: "global",
        text: String(this.$i18n.t("alerts.emptyUpdate")),
        type: "warn",
      });
      return;
    }
    this.currentParams = { id: track._id };
    this.currentTitle = this.trackTitle(track);
    this.currentSubtitle = this.trackArtist(track);
  }

  editTrack(track: ITrack): void {
    this.editingTrack = track;
    this.$bvModal.show("track-edit-modal");
  }

  onTrackSaved(): void {
    this.editingTrack = null;
    this.fetchTracks();
  }

  stopPlayback(): void {
    this.currentParams = {};
    this.currentTitle = "";
    this.currentSubtitle = "";
  }

  async fetchTracks(): Promise<void> {
    if (!this.playlistId) return;
    this.loading = true;
    try {
      const res = await this.$axios.get(`${playlistUrl}/${this.playlistId}`);
      if (res.status === 200) {
        this.tracks = res.data.tracks || [];
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.loading = false;
    }
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    const files = Array.from(input.files);
    input.value = "";

    this.totalBytes = files.reduce((sum, f) => sum + f.size, 0);
    this.uploadedBytes = 0;
    this.uploading = true;
    this.uploadFilesSequentially(files);
  }

  async uploadFilesSequentially(files: File[]): Promise<void> {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      try {
        await this.uploadFile(file);
        this.uploadedBytes += file.size;
      } catch (e) {
        console.log(e);
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("music.uploadError", { name: file.name })),
          type: "warn",
        });
      } finally {
        delete files[i];
      }
    }
    this.uploading = false;
    this.uploadProgress = 0;
    this.fetchTracks();
  }

  uploadFile(file: File): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const url = `${audio.id3Stream}/upload?playlistId=${encodeURIComponent(
        this.playlistId
      )}`;

      const log = (...a: unknown[]) =>
        console.log(`[upl ${file.name.slice(0, 12)}]`, ...a);

      xhr.upload.onprogress = (e: ProgressEvent) => {
        if (!e.lengthComputable) return;
        this.uploadProgress =
          ((this.uploadedBytes + e.loaded) / this.totalBytes) * 100;
        log("progress", e.loaded, "/", e.total);
      };

      xhr.onload = () => {
        log("onload", xhr.status, xhr.responseText?.slice(0, 200));
        if (xhr.status >= 200 && xhr.status < 300) {
          this.$notify({
            group: "global",
            text: String(this.$i18n.t("error.200")),
          });
          resolve();
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => {
        log("onerror");
        reject(new Error("Network error"));
      };

      xhr.onabort = () => {
        log("onabort");
        reject(new Error("Aborted"));
      };

      xhr.ontimeout = () => {
        log("timeout");
        reject(new Error("Timeout"));
      };

      xhr.open("POST", url, true);
      xhr.setRequestHeader(
        "Authorization",
        `Bearer ${this.$store.getters.getAccess}`
      );
      xhr.setRequestHeader("X-File-Name", encodeURIComponent(file.name));
      xhr.setRequestHeader("X-File-Size", String(file.size));
      xhr.timeout = 600000;

      const form = new FormData();
      form.append("file", file, file.name);

      log("start upload", { size: file.size, url });
      xhr.send(form);
    });
  }

  getMimeType(name: string): string | null {
    const ext = name.split(".").pop()?.toLowerCase();
    const map: Record<string, string> = {
      mp3: "audio/mpeg",
      flac: "audio/flac",
      wav: "audio/wav",
      m4a: "audio/mp4",
      ogg: "audio/ogg",
    };
    return ext && map[ext] ? map[ext] : null;
  }

  async removeTrack(trackId: string): Promise<void> {
    const ok = await this.$bvModal.msgBoxConfirm(
      String(this.$t("music.confirmDeleteTrack")),
      {
        title: String(this.$i18n.t("buttons.delete")),
        okVariant: "danger",
        cancelVariant: "secondary",
      }
    );
    if (!ok) return;

    try {
      const res = await this.$axios.delete(`${tracksUrl}/${trackId}/force`);
      if (res.status === 200) {
        this.$notify({
          group: "global",
          text: String(this.$i18n.t("error.200")),
        });
        this.fetchTracks();
      }
    } catch (e) {
      console.log(e);
      this.$notify({
        group: "global",
        text: String(this.$i18n.t("alerts.emptyUpdate")),
        type: "warn",
      });
    }
  }

  mounted(): void {
    this.tracks = Array.isArray(this.initialTracks)
      ? this.initialTracks.slice()
      : [];
  }
}
export default PlaylistTracksEditor;
